import React from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import { ApiHook } from "../../../hooks/apiHook";

const SubscriptionBadge = ({ active, productValidity }) => {
  const dateStr = productValidity ? new Date(productValidity).toLocaleDateString() : "";
  return (
    <span
      className="badge"
      style={{
        backgroundColor: active ? "#1cc88a" : "#e74a3b",
        color: "#fff",
        fontSize: "10px",
      }}
    >
      {active ? `✓ ${dateStr}` : `✗ ${dateStr}`}
    </span>
  );
};

const RankBadge = ({ rankName }) => {
  if (!rankName) return <span className="text-muted" style={{ fontSize: "10px" }}>-</span>;
  return (
    <span className="badge bg-primary" style={{ fontSize: "10px" }}>
      {rankName}
    </span>
  );
};

const PerformerTable = ({ title, data, columns, showDetails = false }) => {
  const { t } = useTranslation();

  return (
    <div className="card shadow-sm border-0 h-100">
      <div className="card-body">
        <h6 className="card-title" style={{ fontSize: "14px" }}>{title}</h6>
        {data && data.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-sm table-hover" style={{ fontSize: "12px" }}>
              <thead>
                <tr>
                  <th style={{ width: "30px" }}>#</th>
                  <th>{t("username")}</th>
                  <th>{t("performerFullName")}</th>
                  {showDetails && <th>{t("rank")}</th>}
                  {showDetails && <th className="text-center">{t("subscriptionStatus")}</th>}
                  {columns.map((col) => (
                    <th key={col.key} className="text-end">{t(col.label)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((item, idx) => (
                  <tr key={item.userId}>
                    <td>
                      {idx < 3 ? (
                        <span
                          className="badge"
                          style={{
                            backgroundColor: idx === 0 ? "#f6c23e" : idx === 1 ? "#c0c0c0" : "#cd7f32",
                            color: "#fff",
                            fontSize: "10px",
                          }}
                        >
                          {idx + 1}
                        </span>
                      ) : (
                        <span className="text-muted">{idx + 1}</span>
                      )}
                    </td>
                    <td>
                      <strong>{item.username}</strong>
                      {showDetails && item.dateOfJoining && (
                        <div className="text-muted" style={{ fontSize: "10px" }}>
                          {t("dateOfJoining")}: {new Date(item.dateOfJoining).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="text-muted">{item.fullName || "-"}</td>
                    {showDetails && (
                      <td><RankBadge rankName={item.rankName} /></td>
                    )}
                    {showDetails && (
                      <td className="text-center">
                        <SubscriptionBadge active={item.subscriptionActive} productValidity={item.productValidity} />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td key={col.key} className="text-end">
                        <strong style={{ color: col.color || "#333" }}>
                          {col.format ? col.format(item[col.key]) : item[col.key]}
                        </strong>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted text-center py-3">{t("noDataAvailable")}</p>
        )}
      </div>
    </div>
  );
};

const TopPerformersSection = ({ startDate, endDate, legId }) => {
  const { t } = useTranslation();
  const { data, isLoading, isFetching, isError } = ApiHook.CallNetworkTopPerformers(startDate, endDate, legId, 10);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" /> {t("loading")}
      </div>
    );
  }

  if (isError || !data?.data) {
    return <div className="alert alert-warning">{t("errorLoadingData")}</div>;
  }

  const { topSellers, topGroupSellers, topRecruiters } = data.data;

  const formatNum = (n) => {
    if (n == null || isNaN(n)) return "0";
    return Number(n).toLocaleString();
  };

  return (
    <div>
      {isFetching && !isLoading && (
        <div className="text-center mb-2">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      <div className="row">
        {/* Top Direct Sellers */}
        <div className="col-md-6 mb-3">
          <PerformerTable
            title={t("topDirectSellers")}
            data={topSellers}
            showDetails
            columns={[
              { key: "directSales", label: "directSales", color: "#1cc88a", format: formatNum },
              { key: "groupSales", label: "groupSales", color: "#4e73df", format: formatNum },
            ]}
          />
        </div>

        {/* Top Group Sellers */}
        <div className="col-md-6 mb-3">
          <PerformerTable
            title={t("topGroupSellers")}
            data={topGroupSellers}
            showDetails
            columns={[
              { key: "groupSales", label: "groupSales", color: "#4e73df", format: formatNum },
              { key: "directSales", label: "directSales", color: "#1cc88a", format: formatNum },
            ]}
          />
        </div>
      </div>

      <div className="row">
        {/* Top Recruiters */}
        <div className="col-12 mb-3">
          <PerformerTable
            title={t("topRecruiters")}
            data={topRecruiters}
            showDetails
            columns={[
              { key: "totalDownline", label: "totalDownline", color: "#1cc88a" },
              { key: "activeDownline", label: "activeDownline", color: "#36b9cc" },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default TopPerformersSection;
